import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { IconPlus, IconDiamond, IconMenu2 } from '@tabler/icons-react'
import { Button } from '@/components/Button.tsx'
import { useClient } from '@/stores/useClient.ts'
import { usePortalContext } from '@/pages/portal/Portal.page.tsx'
import { CreateProjectModal, useCreateProjectModal } from '@/components/portal/CreateProjectModal.tsx'
import { ProjectLimitModal, useProjectLimitModal, isProjectLimitError } from '@/components/portal/ProjectLimitModal.tsx'
import { ProfSubscriptionResponse } from '@/models/profSubscription.ts'

interface PortalTopBarProps {
  onMenuOpen?: () => void
}

export default function PortalTopBar({ onMenuOpen }: PortalTopBarProps) {
  const { getSubscription, createProject } = useClient()
  const portalContext = usePortalContext()
  const queryClient = useQueryClient()
  const { modalRef, show: showCreateModal, close: closeCreateModal } = useCreateProjectModal()
  const { modalRef: limitModalRef, show: showLimitModal } = useProjectLimitModal()
  const [isCreating, setIsCreating] = useState(false)

  const { data: subscription } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const res = await getSubscription()
      return res.data as ProfSubscriptionResponse
    },
    enabled: !!portalContext?.account,
  })

  const showUpgrade = subscription && !subscription.is_paid

  async function handleCreateProject(title: string, templateId: string | null) {
    setIsCreating(true)
    try {
      await createProject(title, templateId)
      closeCreateModal()
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    } catch (e: any) {
      console.error('Error creating project:', e)
      if (isProjectLimitError(e)) {
        closeCreateModal()
        showLimitModal()
      }
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <>
      <div className="h-16 flex items-center justify-between px-3 md:px-5 border-b border-[var(--portal-border)] bg-[var(--portal-bg)] flex-shrink-0">
        <div className="flex items-center gap-3">
          {/* Mobile: hamburger + FS logo */}
          <div className="flex items-center md:hidden">
            <button className="btn btn-ghost btn-sm px-2" onClick={onMenuOpen}>
              <IconMenu2 size={22} />
            </button>
            <Link to="/portal/dashboard">
              <span className="text-xl font-semibold font-serif">PF</span>
            </Link>
          </div>

          <Button size="sm" color="accent" onClick={showCreateModal}>
            <IconPlus size={18} />
            <span className="hidden md:inline">Projekt hinzufügen</span>
            <span className="md:hidden">Projekt</span>
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {showUpgrade && (
            <Link to="/portal/subscription">
              <Button size="sm" color="primary">
                <IconDiamond size={18} />
                <span className="hidden md:inline">Upgrade</span>
                <span className="md:hidden">Preise</span>
              </Button>
            </Link>
          )}
        </div>
      </div>

      <CreateProjectModal
        modalRef={modalRef}
        onConfirm={handleCreateProject}
        isLoading={isCreating}
      />

      <ProjectLimitModal modalRef={limitModalRef} />
    </>
  )
}
